import React, { useState } from "react";
import { ethers } from "ethers";
import { cryptoWaitReady, decodeAddress } from "@polkadot/util-crypto";
import { hexlify } from "@ethersproject/bytes";
import { BigNumberish } from "@ethersproject/bignumber";
import { BytesLike } from "@ethersproject/bytes";
import { InjectedAccountWithMeta } from "@polkadot/extension-inject/types";
import Step from "./Step";
import StepButton from "./StepButton";

type ClaimPayload = {
  chainId: BigNumberish;
  salt: BytesLike;

  substrateAddress?: string | Uint8Array | null;
};

const createClaimPayload = (tx: ClaimPayload) => {
  if (!tx.salt) {
    throw Error("claim payload missing salt");
  }

  if (!tx.chainId) {
    throw Error("claim payload missing chainId");
  }

  if (!tx.substrateAddress) {
    throw Error("claim payload missing substrateAddress");
  }

  let publicKey: Uint8Array;

  try {
    publicKey = decodeAddress(tx.substrateAddress);
  } catch {
    throw Error("invalid substrateAddress");
  }

  return {
    types: {
      EIP712Domain: [
        {
          name: "name",
          type: "string",
        },
        {
          name: "version",
          type: "string",
        },
        {
          name: "chainId",
          type: "uint256",
        },
        {
          name: "salt",
          type: "bytes32",
        },
      ],
      Transaction: [{ name: "substrateAddress", type: "bytes" }],
    },
    primaryType: "Transaction" as const,
    domain: {
      name: "Acala EVM claim",
      version: "1",
      chainId: tx.chainId,
      salt: hexlify(tx.salt),
    },
    message: {
      substrateAddress: hexlify(publicKey),
    },
  };
};

interface Step3Props {
  currentStep: number;
  stepComplete: (signature: string) => void;
  substrateAccount: InjectedAccountWithMeta | null;
  ethAddress: string;
}

const Step3: React.FC<Step3Props> = ({
  currentStep,
  stepComplete,
  substrateAccount,
  ethAddress,
}) => {
  const [loading, setLoading] = useState(false);

  return (
    <Step stepNum={3} currentStep={currentStep} title="Create Claim Signature">
      <div className="flex flex-col justify-center items-start gap-2 py-3">
        <StepButton
          text="Sign"
          completedText="Signed"
          completed={currentStep > 3}
          onClick={async () => {
            setLoading(true);
            try {
              await cryptoWaitReady();
              const provider = new ethers.providers.Web3Provider(
                (window as any).ethereum
              );

              const publicKey = decodeAddress(substrateAccount?.address);

              const payload = createClaimPayload({
                chainId: 595,
                substrateAddress: publicKey,
                salt: "0x5c562e6300954998233c9a40b6b86f3028977e6d32d0da1af207738d19f98c1b",
              });

              const msgParams = JSON.stringify(payload);
              const params = [ethAddress, msgParams];
              const method = "eth_signTypedData_v4";

              const signature = await provider.send(method, params);
              stepComplete(signature);
            } catch {}
            setLoading(false);
          }}
          disabled={currentStep !== 3}
          loading={loading}
        />
      </div>
    </Step>
  );
};

export default Step3;
